import { formulaDependencies, isUsableRange, variableKind } from '@agavi/blocks';

import type { Block } from '@/types/experience';

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Replace {oldName} → {newName} in a formula string. */
export function renameVarInFormula(formula: string, oldName: string, newName: string): string {
  return formula.replace(new RegExp(`\\{${escapeRe(oldName)}\\}`, 'g'), `{${newName}}`);
}

/**
 * Rename a variable's chips in an HTML string (attribute + display text).
 *
 * Matches on the id when the chip carries one, so a rename follows the VARIABLE rather than the
 * text, AND on the name regardless of what id a chip carries.
 *
 * That second half used to be `[data-variable-chip="name"]:not([data-variable-id])` — id-less
 * chips only — to keep two variables that briefly shared a name distinguishable. It silently
 * skipped every chip stamped with some OTHER id, and promotion creates exactly those: absorbing a
 * task variable leaves its chips stamped with the id of a row that no longer exists, so the next
 * rename of the surviving variable passed over all of them. Formulas kept working because they
 * store only `{name}`, which is why this read as "renames in equations but not in richtext".
 *
 * Dropping the `:not()` is safe because the state it guarded against cannot occur: a rename onto
 * a name another variable holds is refused before it reaches here, and two live definitions of
 * one name is the state `variablesInForce` exists to prevent. The comment it replaces even says
 * the sharing was transient — an artefact of the per-keystroke commit that the name field's draft
 * removed.
 */
export function renameVarInHtml(html: string, oldName: string, newName: string, varId?: string): string {
  const byId = varId ? html?.includes(`data-variable-id="${varId}"`) : false;
  if (!byId && !html?.includes(`data-variable-chip="${oldName}"`)) return html;
  const div = document.createElement('div');
  div.innerHTML = html;
  const selector = varId
    ? `[data-variable-id="${varId}"], [data-variable-chip="${oldName}"]`
    : `[data-variable-chip="${oldName}"]`;
  div.querySelectorAll<HTMLElement>(selector).forEach(el => {
    const prev = el.getAttribute('data-variable-chip') ?? '';
    el.setAttribute('data-variable-chip', newName);
    if (el.textContent?.trim() === prev) el.textContent = newName;
  });
  return div.innerHTML;
}

/**
 * Somewhere worth walking into — the ONE decision the two walkers below must agree on.
 *
 * They disagreed for a long time, in the direction that hurts: `someString` recursed into plain
 * objects and `mapStrings` did not, so a chip in `data.feedback.states[].content` (the shape of
 * every feedback-bearing block) was reported as "in use" by the variables panel and then silently
 * missed by the rename. Extracting the test means a future change lands on both at once.
 *
 * Plain objects and arrays only. Anything else carrying a prototype — a Date, a class instance —
 * is a value to preserve rather than a container to take apart, and rebuilding one from its
 * entries would quietly reduce it to `{}`.
 */
function isPlainObject(v: unknown): v is Record<string, unknown> {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
  const proto = Object.getPrototypeOf(v) as unknown;
  return proto === Object.prototype || proto === null;
}

/** Rebuild a value with every string anywhere inside it passed through `fn`. */
function mapStrings(value: unknown, fn: (s: string) => string): unknown {
  if (typeof value === 'string') return fn(value);
  if (Array.isArray(value)) return value.map(item => mapStrings(item, fn));
  if (isPlainObject(value)) {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, mapStrings(v, fn)]));
  }
  return value;
}

/**
 * Whether any string anywhere inside a value satisfies `pred`.
 *
 * Deliberately a separate recursion from `mapStrings` rather than a copy-building one: this runs
 * per variable per block on every render of the variables panel, and answering a boolean by
 * rebuilding every block's data would be a real cost. It keeps the same four-branch shape, and
 * both defer to `isPlainObject` for the only judgement that can drift.
 */
function someString(value: unknown, pred: (s: string) => boolean): boolean {
  if (typeof value === 'string') return pred(value);
  if (Array.isArray(value)) return value.some(item => someString(item, pred));
  if (isPlainObject(value)) {
    return Object.entries(value).some(([key, v]) => !INERT_DATA_KEYS.has(key) && someString(v, pred));
  }
  return false;
}

/**
 * Block data that is kept but no longer DOES anything, and so is not use.
 *
 * `conditionalBranchesDraft` is where a branch set goes when the teacher switches branching off:
 * banked so an accidental untick is recoverable, and deliberately under its own key so no
 * evaluator reads it — its own doc comment says "authoring state, never evaluation state" and
 * "an ignored key cannot mis-grade". The same argument says it must not hold a variable hostage.
 * It can neither grade nor display, so a variable it mentions is not in use.
 *
 * Without this, unchecking branching and leaving the block without reopening it leaves the
 * equation banked in SAVED data, and the variable it referenced can never be deleted again.
 *
 * Only the USAGE scan skips these. A RENAME must still rewrite them, or restoring a banked set
 * later brings back an equation pointing at a name that no longer exists.
 */
const INERT_DATA_KEYS = new Set(['conditionalBranchesDraft']);

/** Recursively rename a variable in all string fields of a block data object. */
export function renameVarInBlockData(
  data: Record<string, unknown>,
  oldName: string,
  newName: string,
  varId?: string,
): Record<string, unknown> {
  return mapStrings(data, s =>
    renameVarInFormula(renameVarInHtml(s, oldName, newName, varId), oldName, newName),
  ) as Record<string, unknown>;
}

/** Check recursively whether any string field in a block data object references varName. */
function isVarUsedInData(data: unknown, varName: string): boolean {
  return someString(
    data,
    s => s.includes(`{${varName}}`) || s.includes(`data-variable-chip="${varName}"`),
  );
}

/**
 * Whether a variable is finished enough to store.
 *
 * A row appears the moment Add is pressed, with no name and nothing filled in, and stays that way
 * while the teacher types — so a half-written one is EDITOR state, not content. Sending it fails
 * validation outright, since a blank name does not match the identifier grammar the server
 * enforces.
 *
 * It used to demand min/max/step, which is what made a constant or an equation impossible to
 * store: neither has a range, so neither ever survived a save. It now asks whether the row is
 * ANY of the three recognised shapes.
 *
 * One definition because there are now two writers. The task's save had this inline; the
 * experience's PATCH did not, and adding a variable there 400'd before the teacher had finished
 * naming it.
 */
export function isSavableVariable(
  v: { name?: string; value?: number; min?: number; max?: number; step?: number; formula?: string },
): boolean {
  const range = { name: v.name ?? '', min: v.min, max: v.max, step: v.step };
  if (!v.name) return false;
  // `variableKind` answers WHICH shape, on field presence alone. Whether that shape is finished
  // is this function's own question, and it has to be asked per kind: a constant is done when it
  // has a number, an equation when it has text, a random when its interval can actually produce
  // something.
  switch (variableKind(v)) {
    case 'constant': return v.value != null && !Number.isNaN(v.value);
    case 'equation': return !!v.formula?.trim();
    case 'random':   return isUsableRange(range);
  }
}

/**
 * The variables in force for one task: the experience's, plus the task's own that do not collide.
 *
 * EXPERIENCE WINS, and a collision is a state the authoring flow refuses to create — creating a
 * task variable with an experience variable's name is refused outright, and promoting a variable
 * to the experience absorbs the task copies rather than shadowing them. This function is what
 * happens if one shows up anyway, which another tab can still produce: the experience definition
 * is the one in force and the task's is inert.
 *
 * The reason it cannot be the other way round is the same reason a rename onto a taken name is
 * refused: resolution is BY NAME, from the block's `{name}` to the roll map's key, so two live
 * definitions of one name is not a state anything downstream could represent.
 */
export function variablesInForce<V extends { name: string }>(experienceVars: V[], taskVars: V[]): V[] {
  const taken = new Set(experienceVars.map(v => v.name));
  return [...experienceVars, ...taskVars.filter(v => !taken.has(v.name))];
}

/**
 * The same precedence as `variablesInForce`, applied to two ROLLS rather than two definitions.
 *
 * A recalled answer is resolved against its own task's variables, so the page draws the
 * experience's once — shared, because they are one definition and two tasks quoting one of them
 * must agree — and each task's own separately. Merging them is where the precedence has to be
 * restated, and restating it by hand at the call site is how the two come to disagree about a
 * name: `variablesInForce` would say the experience's definition is the live one while the roll
 * handed to the evaluator carried the task's value for it.
 */
export function rollsInForce(
  taskRoll: Record<string, number>,
  experienceRoll: Record<string, number>,
): Record<string, number> {
  return { ...taskRoll, ...experienceRoll };
}

/** The task variables an experience variable is currently overriding — nothing authored should
 *  produce one, so each is a problem to report rather than a state to live with. */
export function shadowedTaskVariables<V extends { name: string }>(experienceVars: V[], taskVars: V[]): V[] {
  const taken = new Set(experienceVars.map(v => v.name));
  return taskVars.filter(v => taken.has(v.name));
}

/** Returns true if varName is referenced in any block's data. */
export function isVarUsed(varName: string, blocks: Block[]): boolean {
  return blocks.some(b => isVarUsedInData(b.data, varName));
}

/**
 * How a variable's DEFINITION reads wherever one has to be shown as prose — the paste conflict
 * dialog and the promotion dialog, which both answer "what will this become".
 *
 * One derivation, because it was two: `describe` in `paste-conflict-dialog.tsx` and
 * `describeRange` in the page, identical and separately maintained, and NEITHER knew about a
 * constant — a `{g} = 9.8` rendered as "undefined to undefined, precision undefined".
 *
 * It says "precision", not "step". The panel's own column has always been headed Precision
 * because "step" is a programmer's word for it, and a report that names the same number
 * differently from the field a teacher sets it in is a report about something else as far as
 * they can tell.
 */
export function describeDefinition(
  v: { value?: number; min?: number; max?: number; step?: number; formula?: string },
  t: (k: string, o?: Record<string, unknown>) => string,
): string {
  switch (variableKind(v)) {
    case 'equation': return t('task.paste.conflict.formula', { formula: v.formula });
    case 'constant': return t('task.paste.conflict.value', { value: v.value });
    case 'random':   return t('task.paste.conflict.range', { min: v.min, max: v.max, step: v.step });
  }
}

/**
 * WHICH blocks reference this variable, in order, so a teacher can be told where to look.
 *
 * `isVarUsed` answers only yes or no, which is all a disabled button strictly needs — but
 * "cannot delete" without "where" sends them hunting through every question in the task.
 */
export function blocksUsingVar(varName: string, blocks: Block[]): Block[] {
  if (!varName) return [];
  return blocks.filter(b => isVarUsedInData(b.data, varName));
}

/**
 * Does another VARIABLE depend on this one — `luminosity = {mass}^3.5` depending on `mass`?
 *
 * The companion to `isVarUsed`, which only ever walked block data. That was complete while a
 * variable could not reference another one; an equation makes a second kind of dependent, and
 * every place that asks "is this still in use" has to ask about both or it is answering a
 * narrower question than the teacher thinks. Deleting `mass` out from under `luminosity` leaves
 * an equation that silently resolves to nothing.
 *
 * Self-reference is excluded: a formula naming itself is a cycle, which resolution already
 * refuses, and counting it here would make a variable undeletable by its own mistake.
 */
export function isVarUsedByFormula(
  varName: string,
  variables: { name: string; formula?: string }[],
): boolean {
  return !!firstDependentOf(varName, variables);
}

/** The first variable whose equation stands on this one, so the refusal can name it. */
export function firstDependentOf(
  varName: string,
  variables: { name: string; formula?: string }[],
): string | undefined {
  if (!varName) return undefined;
  return variables.find(v =>
    v.name !== varName && !!v.formula && formulaDependencies(v.formula).includes(varName))?.name;
}

/**
 * The task variables a single block actually references — what a copy has to carry with it.
 *
 * Deliberately the same predicate the variables panel uses to decide whether a variable is in use,
 * so a block cannot be copied without a variable that the panel would have called used.
 */
export function variablesUsedByBlock<V extends { name: string; formula?: string }>(
  block: Block, variables: V[],
): V[] {
  const wanted = new Set<string>();
  const byName = new Map(variables.filter(v => !!v.name).map(v => [v.name, v]));

  // TRANSITIVE, because a copied block mentioning `{luminosity}` needs `{mass}` to arrive with it.
  // Carrying only the names the block itself spells out would paste an equation whose seeds are
  // missing, and `resolveVariables` omits an equation it cannot compute — so the chip would land
  // in the new task and quietly render as a chip forever.
  const stack = [...byName.keys()].filter(name => isVarUsedInData(block.data, name));
  while (stack.length > 0) {
    const name = stack.pop()!;
    if (wanted.has(name)) continue;
    wanted.add(name);
    const v = byName.get(name);
    // `wanted` guards the repeat visit, so a cycle among the formulas terminates here as well.
    if (v?.formula) stack.push(...formulaDependencies(v.formula).filter(dep => byName.has(dep)));
  }

  // Source order, not discovery order: the paste appends these to the destination's list and a
  // teacher should find them in the order they kept them in.
  return variables.filter(v => wanted.has(v.name));
}

/** Where one copied variable reference should now point. */
export interface VarBinding {
  /** The chip id as authored in the copied block. Absent for chips predating `data-variable-id`. */
  fromId?: string;
  fromName: string;
  toId: string;
  toName: string;
}

/**
 * Re-point a copied block's variable references at the destination task's variables.
 *
 * Distinct from renaming, which moves a variable the block already belongs to: this moves the
 * BLOCK to a different task's variables, so both the name and the id change, and the id is what
 * decides which numbers a student sees (`deriveSeed` hashes the variable id, so leaving a stale id
 * behind would silently roll values from a variable this task does not have).
 *
 * ONE pass over each string, not a rename per variable. Sequential renames cascade whenever two
 * variables trade names — an incoming `a`→`b` applied before `b`→`c` would run the first result
 * through the second — and a block carrying several variables into a task that already has some of
 * them produces exactly that. Resolving every binding in a single substitution cannot cascade.
 */
export function rebindVarsInBlockData(
  data: Record<string, unknown>,
  bindings: VarBinding[],
): Record<string, unknown> {
  if (!bindings.length) return data;
  const byId = new Map(bindings.filter(b => b.fromId).map(b => [b.fromId!, b]));
  const byName = new Map(bindings.map(b => [b.fromName, b]));

  return mapStrings(data, s => {
    let out = s;
    if (out.includes('data-variable-chip=')) {
      const div = document.createElement('div');
      div.innerHTML = out;
      let touched = false;
      div.querySelectorAll<HTMLElement>('[data-variable-chip]').forEach(el => {
        const name = el.getAttribute('data-variable-chip') ?? '';
        const id = el.getAttribute('data-variable-id') ?? undefined;
        // Id first: a chip that carries one is authoritative even when its name is also changing.
        const b = (id ? byId.get(id) : undefined) ?? byName.get(name);
        if (!b) return;
        touched = true;
        el.setAttribute('data-variable-chip', b.toName);
        el.setAttribute('data-variable-id', b.toId);
        if (el.textContent?.trim() === name) el.textContent = b.toName;
      });
      if (touched) out = div.innerHTML;
    }
    // Formula fields reference by name only — there is no id to follow — so they resolve by name,
    // still in one substitution. A name with no binding is left exactly as authored.
    if (out.includes('{')) {
      out = out.replace(/\{([A-Za-z][A-Za-z0-9_]*)\}/g, (whole, name: string) =>
        byName.has(name) ? `{${byName.get(name)!.toName}}` : whole,
      );
    }
    return out;
  }) as Record<string, unknown>;
}
